import React, { useState, useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../context/AuthContext';
import Layout from '../../components/Layout/Layout';
import { Card, Button, Select, Badge } from '../../components/UI';
import QuickStartGuide, { DISMISSED_KEY } from '../../components/QuickStartGuide';
import TodayOverviewKPIGrid from '../../components/KPI/TodayOverviewKPIGrid';
import { dashboardAPI, leadsAPI, callLogsAPI, reportsAPI, pipelineAPI, productsAPI } from '../../services/api';
import toast from 'react-hot-toast';
import {
  HiOutlineDownload, HiOutlineUsers, HiOutlinePhone,
  HiOutlinePhoneIncoming, HiOutlinePhoneMissedCall, HiOutlineTrendingUp,
  HiOutlineCalendar, HiOutlineCheckCircle, HiOutlineClock, HiOutlineFire,
  HiOutlineClipboardCheck, HiOutlineSwitchHorizontal, HiOutlineChartPie,
  HiOutlineExclamation, HiOutlineDocumentText, HiOutlineArrowRight, HiOutlineRefresh,
  HiOutlineUser,
} from 'react-icons/hi';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  Legend, PieChart, Pie, Cell, BarChart, Bar, FunnelChart, Funnel, LabelList,
} from 'recharts';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { format } from 'date-fns';
import { fixAlertGrammar, formatLabel } from '../../utils/formatLabel';

const StatCard = ({ title, value, icon, trend, trendValue, trendLabel = 'vs last period', color, onClick }) => {
  const iconChipClasses = {
    primary: 'from-primary-500 to-primary-600 ring-primary-500/20',
    indigo: 'from-indigo-500 to-indigo-600 ring-indigo-500/20',
    info: 'from-blue-500 to-blue-600 ring-blue-500/20',
    success: 'from-green-500 to-green-600 ring-green-500/20',
    danger: 'from-red-500 to-red-600 ring-red-500/20',
    orange: 'from-orange-500 to-orange-600 ring-orange-500/20',
    warning: 'from-amber-500 to-amber-600 ring-amber-500/20',
  };
  const accentClasses = {
    primary: 'bg-gradient-to-r from-primary-500 via-primary-400 to-transparent',
    indigo: 'bg-gradient-to-r from-indigo-500 via-indigo-400 to-transparent',
    info: 'bg-gradient-to-r from-blue-500 via-blue-400 to-transparent',
    success: 'bg-gradient-to-r from-green-500 via-green-400 to-transparent',
    danger: 'bg-gradient-to-r from-red-500 via-red-400 to-transparent',
    orange: 'bg-gradient-to-r from-orange-500 via-orange-400 to-transparent',
    warning: 'bg-gradient-to-r from-amber-500 via-amber-400 to-transparent',
  };
  const glowClasses = {
    primary: 'bg-primary-500',
    indigo: 'bg-indigo-500',
    info: 'bg-blue-500',
    success: 'bg-green-500',
    danger: 'bg-red-500',
    orange: 'bg-orange-500',
    warning: 'bg-amber-500',
  };

  // Support both old format (trend='up', trendValue='12%') and new format (trend={trend: 'up', value: '12%', label: 'vs last period'})
  const trendData = typeof trend === 'object' ? trend : (trend ? { trend, value: trendValue, label: trendLabel } : null);

  return (
    <div
      onClick={onClick}
      className={`relative overflow-hidden bg-white rounded-2xl border border-gray-100 p-4 shadow-md hover:shadow-xl hover:shadow-gray-200/70 hover:-translate-y-1 transition-all duration-300 group ${onClick ? 'cursor-pointer' : ''}`}
    >
      {/* Soft color glow in the corner */}
      <div className={`absolute -top-8 -right-8 w-24 h-24 rounded-full blur-3xl opacity-20 group-hover:opacity-40 transition-opacity duration-300 ${glowClasses[color] || glowClasses.primary}`} />

      {/* Colored accent bar fading in from the top edge */}
      <div className={`absolute inset-x-0 top-0 h-1 ${accentClasses[color] || accentClasses.primary}`} />

      <div className="relative flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 leading-snug line-clamp-2" title={typeof title === 'string' ? title : undefined}>{title}</p>
          <p className="text-2xl font-extrabold tracking-tight text-gray-900 leading-tight mt-1.5">{value}</p>
        </div>
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 shadow-md ring-4 bg-gradient-to-br transition-transform duration-300 group-hover:scale-110 ${iconChipClasses[color] || iconChipClasses.primary}`}>
          <span className="text-white">{icon}</span>
        </div>
      </div>
      {trendData && (
        <div className="relative mt-2.5 flex items-center gap-1.5 min-w-0">
          <span className={`inline-flex items-center gap-0.5 text-[11px] font-semibold px-1.5 py-0.5 rounded-full flex-shrink-0 ${trendData.trend === 'up' ? 'text-green-700 bg-green-50' : 'text-red-700 bg-red-50'}`}>
            {trendData.trend === 'up' ? '↑' : '↓'} {trendData.value}
          </span>
          <span className="text-[11px] text-gray-400 truncate">{trendData.label}</span>
        </div>
      )}
      {/* Tooltip on hover */}
      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-[100]">
        {title}
      </div>
    </div>
  );
};

const ALERT_STYLES = {
  danger: { bg: 'bg-red-50', border: 'border-red-100', icon: 'bg-red-500', iconColor: <HiOutlineExclamation className="w-5 h-5 text-white" /> },
  warning: { bg: 'bg-orange-50', border: 'border-orange-100', icon: 'bg-orange-500', iconColor: <HiOutlineFire className="w-5 h-5 text-white" /> },
  caution: { bg: 'bg-amber-50', border: 'border-amber-100', icon: 'bg-amber-500', iconColor: <HiOutlineClock className="w-5 h-5 text-white" /> },
  info: { bg: 'bg-indigo-50', border: 'border-indigo-100', icon: 'bg-indigo-500', iconColor: <HiOutlineDocumentText className="w-5 h-5 text-white" /> },
  success: { bg: 'bg-green-50', border: 'border-green-100', icon: 'bg-green-500', iconColor: <HiOutlineChartPie className="w-5 h-5 text-white" /> },
};

const AlertItem = ({ type = 'info', title, description, onClick }) => {
  const s = ALERT_STYLES[type] || ALERT_STYLES.info;
  return (
    <div
      onClick={onClick}
      className={`flex items-center gap-3 p-3 rounded-xl border shadow-sm cursor-pointer hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 group ${s.bg} ${s.border}`}
    >
      <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 shadow-sm ${s.icon}`}>
        {s.iconColor}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-gray-900 truncate" title={typeof title === 'string' ? title : undefined}>{title}</p>
        <p className="text-xs text-gray-500 truncate" title={typeof description === 'string' ? description : undefined}>{description}</p>
      </div>
      <HiOutlineArrowRight className="w-4 h-4 text-gray-400 flex-shrink-0 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:text-gray-600" />
    </div>
  );
};

// Section-card header band colors (a different format from the stat cards)
const BAND_STYLES = {
  primary: 'bg-primary-50 border-primary-100 text-primary-700',
  indigo: 'bg-indigo-50 border-indigo-100 text-indigo-700',
  info: 'bg-blue-50 border-blue-100 text-blue-700',
  success: 'bg-green-50 border-green-100 text-green-700',
  danger: 'bg-red-50 border-red-100 text-red-700',
  orange: 'bg-orange-50 border-orange-100 text-orange-700',
  warning: 'bg-amber-50 border-amber-100 text-amber-700',
};
const CHIP_STYLES = {
  primary: 'from-primary-500 to-primary-600 ring-primary-500/20',
  indigo: 'from-indigo-500 to-indigo-600 ring-indigo-500/20',
  info: 'from-blue-500 to-blue-600 ring-blue-500/20',
  success: 'from-green-500 to-green-600 ring-green-500/20',
  danger: 'from-red-500 to-red-600 ring-red-500/20',
  orange: 'from-orange-500 to-orange-600 ring-orange-500/20',
  warning: 'from-amber-500 to-amber-600 ring-amber-500/20',
};

// Header band for section/graph cards: tinted strip across the top edge
// (negative margins pull it flush against the card's rounded corners)
const PanelHeader = ({ icon, title, accent = 'primary', right }) => (
  <div className={`flex items-center justify-between gap-3 -mx-4 sm:-mx-6 -mt-4 sm:-mt-6 mb-4 px-4 sm:px-6 py-3 border-b ${BAND_STYLES[accent] || BAND_STYLES.primary}`}>
    <div className="flex items-center gap-2.5 min-w-0">
      <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm bg-gradient-to-br ${CHIP_STYLES[accent] || CHIP_STYLES.primary}`}>
        <span className="text-white">{icon}</span>
      </div>
      <h3 className="panel-title font-semibold truncate">{title}</h3>
    </div>
    {right}
  </div>
);

const PIE_COLORS = ['#3B82F6', '#10B981', '#8B5CF6', '#F97316', '#EC4899', '#6B7280'];

const Dashboard = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated, isInitialized } = useAuth();
  const [dateRange, setDateRange] = useState('week');
  const [source, setSource] = useState('all');
  const [product, setProduct] = useState('all');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [quickStartOpen, setQuickStartOpen] = useState(false);

  // Auto-show Quick Start Guide when requested via query param (?guide=open)
  useEffect(() => {
    if (isAuthenticated && isInitialized && searchParams.get('guide') === 'open') {
      setQuickStartOpen(true);
      setSearchParams({}, { replace: true }); // Clean the URL
    }
  }, [isAuthenticated, isInitialized, searchParams, setSearchParams]);

  // Auto-show Quick Start Guide on first visit
  useEffect(() => {
    if (isAuthenticated && isInitialized) {
      const dismissed = localStorage.getItem(DISMISSED_KEY);
      if (!dismissed) {
        const timer = setTimeout(() => setQuickStartOpen(true), 1200);
        return () => clearTimeout(timer);
      }
    }
  }, [isAuthenticated, isInitialized]);

  // Helper function to get previous period dates
  const getPreviousPeriodParams = () => {
    const now = new Date();
    let prevStartDate, prevEndDate;

    if (dateRange === 'custom' && customStartDate && customEndDate) {
      // For custom range, calculate the same length previous period
      const start = new Date(customStartDate);
      const end = new Date(customEndDate);
      const daysDiff = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
      prevEndDate = new Date(start);
      prevEndDate.setDate(prevEndDate.getDate() - 1);
      prevStartDate = new Date(prevEndDate);
      prevStartDate.setDate(prevStartDate.getDate() - daysDiff + 1);
    } else if (dateRange === 'week') {
      // Previous week
      prevEndDate = new Date(now);
      prevEndDate.setDate(prevEndDate.getDate() - 7);
      prevStartDate = new Date(prevEndDate);
      prevStartDate.setDate(prevStartDate.getDate() - 6);
    } else if (dateRange === 'month') {
      // Previous month
      prevStartDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      prevEndDate = new Date(now.getFullYear(), now.getMonth(), 0);
    } else if (dateRange === 'quarter') {
      // Previous quarter
      const currentQuarter = Math.floor(now.getMonth() / 3);
      prevStartDate = new Date(now.getFullYear(), (currentQuarter - 1) * 3, 1);
      prevEndDate = new Date(now.getFullYear(), currentQuarter * 3, 0);
    } else if (dateRange === 'last_quarter') {
      // Two quarters ago
      const currentQuarter = Math.floor(now.getMonth() / 3);
      prevStartDate = new Date(now.getFullYear(), (currentQuarter - 2) * 3, 1);
      prevEndDate = new Date(now.getFullYear(), (currentQuarter - 1) * 3, 0);
    } else {
      // Default: previous period same as current
      prevStartDate = new Date(now);
      prevEndDate = new Date(now);
    }

    return {
      startDate: prevStartDate.toISOString().split('T')[0],
      endDate: prevEndDate.toISOString().split('T')[0]
    };
  };

  // Get effective date range for API calls
  const getEffectiveDateRange = () => {
    if (dateRange === 'custom' && customStartDate && customEndDate) {
      return { startDate: customStartDate, endDate: customEndDate };
    }
    return { range: dateRange };
  };

  // ---- Data fetching ----

  // Get effective date range params for API calls
  const getDateParams = () => {
    if (dateRange === 'custom' && customStartDate && customEndDate) {
      return { startDate: customStartDate, endDate: customEndDate };
    }
    return { range: dateRange };
  };

  const dateParams = getDateParams();
  const dateQueryKey = dateRange === 'custom' ? `${customStartDate}-${customEndDate}` : dateRange;

  // Check if custom date range is valid (both dates present and To >= From)
  const isCustomDateValid = dateRange !== 'custom' || (Boolean(customStartDate) && Boolean(customEndDate) && customEndDate >= customStartDate);
  const isQueryEnabled = isAuthenticated && isInitialized && isCustomDateValid;

  // Fetch previous period data for trend calculation
  const prevPeriodParams = getPreviousPeriodParams();
  const { data: prevStatsData } = useQuery({
    queryKey: ['dashboardStatsPrev', prevPeriodParams.startDate, prevPeriodParams.endDate, source, product],
    queryFn: async () => {
      try {
        const response = await dashboardAPI.getStats({ ...prevPeriodParams, source, product });
        return response.data?.data || {};
      } catch (error) {
        return {};
      }
    },
    enabled: isQueryEnabled,
  });

  const { data: prevCallStats } = useQuery({
    queryKey: ['callStatsPrev', prevPeriodParams.startDate, prevPeriodParams.endDate, source, product],
    queryFn: async () => {
      try {
        const response = await callLogsAPI.getStatistics({ source: 'app', ...prevPeriodParams, leadSource: source, product });
        return response.data?.data || {};
      } catch (error) {
        return {};
      }
    },
    enabled: isQueryEnabled,
  });

  // Calculate percentage change
  const calculateTrend = (current, previous) => {
    if (!previous || previous === 0) {
      return current > 0 ? { trend: 'up', value: '100%', label: 'vs last period' } : null;
    }
    const change = ((current - previous) / previous) * 100;
    const roundedChange = Math.round(change);
    return {
      trend: change >= 0 ? 'up' : 'down',
      value: `${Math.abs(roundedChange)}%`,
      label: 'vs last period'
    };
  };

  const { data: statsData, isLoading: statsLoading, isFetching: statsFetching, refetch: refetchStats } = useQuery({
    queryKey: ['dashboardStats', dateQueryKey, source, product],
    queryFn: async () => {
      try {
        const response = await dashboardAPI.getStats({ ...dateParams, source, product });
        return response.data?.data || {};
      } catch (error) {
        return {};
      }
    },
    enabled: isQueryEnabled,
  });

  const { data: leadsCounts, isFetching: leadsFetching, refetch: refetchLeadsCounts } = useQuery({
    queryKey: ['leadsCounts', source, product],
    queryFn: async () => {
      try {
        // Lead counts show ALL leads (not date-filtered) - shows current pipeline status
        const response = await leadsAPI.getCounts({ source, product });
        return response.data?.data || {};
      } catch (error) {
        return {};
      }
    },
    enabled: isAuthenticated && isInitialized,
  });

  const { data: callStats, isFetching: callFetching, refetch: refetchCallStats } = useQuery({
    queryKey: ['callStats', 'app', dateQueryKey, source, product],
    queryFn: async () => {
      try {
        const response = await callLogsAPI.getStatistics({ source: 'app', ...dateParams, leadSource: source, product });
        return response.data?.data || {};
      } catch (error) {
        return {};
      }
    },
    enabled: isQueryEnabled,
  });

  const { data: stageStats, isFetching: stageFetching, refetch: refetchStageStats } = useQuery({
    queryKey: ['stageStats', source, product],
    queryFn: async () => {
      try {
        // Pipeline stats show ALL leads (not date-filtered) - shows current pipeline status
        const response = await pipelineAPI.getStats({ source, product });
        return response.data?.data || [];
      } catch (error) {
        return [];
      }
    },
    enabled: isAuthenticated && isInitialized,
  });

  // Daily Activity Trend — Leads Created / Connected Calls / Follow-ups Completed
  // Automatically switches to weekly/monthly based on date range
  const { data: dailyActivityResponse, isFetching: dailyFetching, refetch: refetchDailyActivity } = useQuery({
    queryKey: ['dailyActivity', dateQueryKey, source, product],
    queryFn: async () => {
      try {
        const response = await reportsAPI.getDailyCalls({ ...dateParams, source, product });
        // expected shape: { data: [{ date, leadsCreated, connectedCalls, followUpsCompleted }, ...], aggregationType: 'daily'|'weekly'|'monthly' }
        return response.data || { data: [], aggregationType: 'daily' };
      } catch (error) {
        return { data: [], aggregationType: 'daily' };
      }
    },
    enabled: isQueryEnabled,
  });

  // Extract data and aggregation type
  const dailyActivityData = dailyActivityResponse?.data || [];
  const activityAggregationType = dailyActivityResponse?.aggregationType || 'daily';

  // Get chart title based on aggregation type
  const getActivityChartTitle = () => {
    switch (activityAggregationType) {
      case 'monthly':
        return 'Monthly Activity Trend';
      case 'weekly':
        return 'Weekly Activity Trend';
      default:
        return 'Daily Activity Trend';
    }
  };

  // Lead Source Performance (donut)
  const { data: leadSourceData = [], isFetching: leadSourceFetching, refetch: refetchLeadSources } = useQuery({
    queryKey: ['leadSourcePerformance', dateQueryKey, source, product],
    queryFn: async () => {
      try {
        const response = await reportsAPI.getLeadSources({ ...dateParams, source, product });
        // expected shape: [{ name, value, color }, ...]
        return response.data?.data || [];
      } catch (error) {
        console.error('Lead sources error:', error);
        return [];
      }
    },
    enabled: isQueryEnabled,
  });

  // CEO Alerts
  const { data: ceoAlerts = [], isFetching: alertsFetching, refetch: refetchAlerts } = useQuery({
    queryKey: ['ceoAlerts'],
    queryFn: async () => {
      try {
        const response = await dashboardAPI.getAlerts?.();
        // expected shape: [{ id, type, title, description, link }, ...]
        const list = response?.data?.data || [];
        return Array.isArray(list) ? list.map((a) => ({ ...a, title: fixAlertGrammar(a.title) })) : [];
      } catch (error) {
        return [];
      }
    },
    enabled: isAuthenticated && isInitialized,
  });

  // Daily Performance Summary — fetch today's daily work report
  const todayStr = format(new Date(), 'yyyy-MM-dd');
  const { data: dailyPerformanceData } = useQuery({
    queryKey: ['dailyWorkReport', todayStr],
    queryFn: async () => {
      try {
        const response = await reportsAPI.getDailyWorkReport({ date: todayStr });
        return response.data?.data || {};
      } catch (error) {
        return {};
      }
    },
    enabled: isAuthenticated && isInitialized,
  });

  // Top Agent Performance — follows its own date picker (defaults to today;
  // reuses the same query cache when the picked date is today)
  const [agentDate, setAgentDate] = useState(todayStr);
  const isAgentToday = agentDate === todayStr;
  const { data: agentDayData } = useQuery({
    queryKey: ['dailyWorkReport', agentDate],
    queryFn: async () => {
      try {
        const response = await reportsAPI.getDailyWorkReport({ date: agentDate });
        return response.data?.data || {};
      } catch (error) {
        return {};
      }
    },
    enabled: isAuthenticated && isInitialized,
  });

  // Fetch products for the filter dropdown — ALL products of the organization
  // (active and inactive, so every product is selectable for historical data)
  const { data: productsData = [] } = useQuery({
    queryKey: ['productsList'],
    queryFn: async () => {
      try {
        const response = await productsAPI.getAll();
        return response.data?.data || [];
      } catch (error) {
        return [];
      }
    },
    enabled: isAuthenticated && isInitialized,
  });

  // Fetch all unique sources dynamically from database
  const { data: sourcesData = [] } = useQuery({
    queryKey: ['uniqueLeadSources'],
    queryFn: async () => {
      try {
        const response = await leadsAPI.getSources();
        return response.data?.data || [];
      } catch (error) {
        console.error('Error fetching sources:', error);
        return [];
      }
    },
    enabled: isAuthenticated && isInitialized,
  });

  // Source options for dropdown - dynamically loaded from database
  const sourceOptions = useMemo(() => {
    const options = [{ value: 'all', label: 'All Sources' }];
    if (sourcesData && sourcesData.length > 0) {
      sourcesData.forEach((source) => {
        if (source && source.trim() !== '') {
          options.push({ value: source, label: source });
        }
      });
    }
    return options;
  }, [sourcesData]);

  // Product options for dropdown — every product of the organization appears
  // here (inactive ones are labeled). When nothing has been created yet, a
  // disabled hint explains why the list shows only "All Products".
  const productOptions = useMemo(() => {
    const options = [{ value: 'all', label: 'All Products' }];
    if (productsData && productsData.length > 0) {
      productsData.forEach((product) => {
        const label = product.isActive === false ? `${product.name} (inactive)` : product.name;
        options.push({ value: product._id, label });
      });
    } else {
      options.push({
        value: '__none__',
        label: 'No products yet — add them in the Products section',
        disabled: true,
      });
    }
    return options;
  }, [productsData]);

  // Lead Funnel Overview - sorted by count (descending)
  const funnelData = React.useMemo(() => {
    if (!stageStats || stageStats.length === 0) return [];
    // Sort by count descending - highest at top, lowest at bottom
    const sorted = [...stageStats].sort((a, b) => (b.leadsCount || 0) - (a.leadsCount || 0));
    return sorted.map((s, idx) => ({
      name: s.name,
      value: s.leadsCount || 0,
      fill: s.color || PIE_COLORS[idx % PIE_COLORS.length],
    }));
  }, [stageStats]);

  // Only show loading during initial load, not during refetches
  const isLoading = statsLoading && !statsData;

  const handleRefresh = () => {
    refetchStats();
    refetchLeadsCounts();
    refetchCallStats();
    refetchStageStats();
    refetchDailyActivity();
    refetchLeadSources();
    refetchAlerts();
    toast.success('Dashboard refreshed');
  };

  const handleExport = async () => {
    try {
      toast.loading('Generating report...');

      // Build CSV data from dashboard stats
      const csvRows = [];

      // Header row
      csvRows.push(['Dashboard Report']);
      csvRows.push([`Date Range: ${dateRange}`]);
      csvRows.push([`Generated: ${new Date().toLocaleString()}`]);
      csvRows.push([]);

      // Summary stats
      csvRows.push(['KEY METRICS']);
      csvRows.push(['Metric', 'Value']);
      csvRows.push(['Total Leads', totalLeads]);
      csvRows.push(["Today's Leads", stats?.todaysLeads || 0]);
      csvRows.push(['Calls Today', stats?.callsToday || 0]);
      csvRows.push(['Connected Calls', callStats?.connected || 0]);
      csvRows.push(['Not Connected', callStats?.notConnected || 0]);
      csvRows.push(['Connection Rate', `${connectionRate}%`]);
      csvRows.push(['Follow-ups Due Today', stats?.followUpsDueToday || 0]);
      csvRows.push(['Follow-ups Completed', stats?.followUpsCompleted || 0]);
      csvRows.push(['Hot Leads', stats?.hotLeads || 0]);
      csvRows.push(['Converted Leads', convertedCount]);
      csvRows.push(['Conversion Rate', `${conversionRate}%`]);
      csvRows.push([]);

      // Lead Source Performance
      if (leadSourceData && leadSourceData.length > 0) {
        csvRows.push(['LEAD SOURCE PERFORMANCE']);
        csvRows.push(['Source', 'Leads', 'Calls', 'Connected', 'Converted', 'Lost']);
        leadSourceData.forEach((item) => {
          csvRows.push([item.name, item.value || item.leads || 0, item.calls || 0, item.connected || 0, item.converted || 0, item.lost || 0]);
        });
        csvRows.push([]);
      }

      // Daily Activity Trend
      if (dailyActivityData && dailyActivityData.length > 0) {
        csvRows.push(['DAILY ACTIVITY TREND']);
        csvRows.push(['Date', 'Leads Created', 'Connected Calls', 'Follow-ups Completed']);
        dailyActivityData.forEach((item) => {
          csvRows.push([item.date || item.fullDate, item.leadsCreated || item.leads || 0, item.connected || item.connectedCalls || 0, item.followUpsCompleted || 0]);
        });
        csvRows.push([]);
      }

      // Pipeline Stages
      if (stageStats && stageStats.length > 0) {
        csvRows.push(['PIPELINE STAGES']);
        csvRows.push(['Stage', 'Leads Count']);
        stageStats.forEach((stage) => {
          csvRows.push([stage.name, stage.leadsCount || stage.count || 0]);
        });
        csvRows.push([]);
      }

      // CEO Alerts
      if (ceoAlerts && ceoAlerts.length > 0) {
        csvRows.push(['CEO ALERTS']);
        csvRows.push(['Type', 'Title', 'Description']);
        ceoAlerts.forEach((alert) => {
          csvRows.push([alert.type, alert.title, alert.description]);
        });
      }

      // Convert to CSV string
      const csvContent = csvRows.map((row) => row.map((cell) => `"${cell}"`).join(',')).join('\n');

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `dashboard-report-${dateRange}-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.dismiss();
      toast.success('Report exported successfully');
    } catch (error) {
      toast.dismiss();
      toast.error('Failed to export report');
      console.error('Export error:', error);
    }
  };

  const stats = statsData || {};

  // Total Leads comes from the same dashboard response as Today's Leads, so
  // the two cards can never go out of sync (previously they were cached by
  // two separate React Query caches that refreshed at different times).
  const totalLeads = stats.allTimeTotalLeads != null
    ? stats.allTimeTotalLeads
    : (stageStats?.length > 0
      ? stageStats.reduce((sum, s) => sum + (s.leadsCount || 0), 0)
      : (leadsCounts ? Object.values(leadsCounts).reduce((a, b) => a + (b || 0), 0) : 0));

  // Total leads from lead source data (for Lead Source Performance chart)
  // Ensure we have valid data with values
  const validLeadSourceData = useMemo(() => {
    if (!leadSourceData || leadSourceData.length === 0) return [];
    // Filter out items with no or zero value, and ensure each item has required properties
    return leadSourceData
      .filter(item => item && (item.value > 0 || item.leads > 0))
      .map(item => ({
        name: (!item.name || item.name === 'undefined' || item.name.trim() === '') ? 'Unknown' : item.name,
        value: item.value || item.leads || 0,
        color: item.color,
        leads: item.leads || item.value || 0,
        calls: item.calls || 0,
        connected: item.connected || 0,
        converted: item.converted || 0,
        lost: item.lost || 0,
      }));
  }, [leadSourceData]);

  const totalLeadSources = validLeadSourceData.length > 0
    ? validLeadSourceData.reduce((sum, item) => sum + item.value, 0)
    : 0;

  const convertedStage = stageStats?.find((s) => s.key === 'converted');
  const convertedCount = convertedStage?.leadsCount || leadsCounts?.converted || 0;
  const conversionRate = totalLeads > 0 ? ((convertedCount / totalLeads) * 100).toFixed(1) : '0.0';
  const connectionRate = callStats?.total > 0 ? ((callStats.connected / callStats.total) * 100).toFixed(1) : '0.0';


  return (
    <Layout>
      <div className="p-4 lg:p-6 space-y-6 relative">
        {/* Loading overlay - only show during initial load */}
        {isLoading && (
          <div className="fixed inset-0 bg-white/80 backdrop-blur-sm z-50 flex items-center justify-center">
            <div className="flex flex-col items-center gap-3">
              <svg className="animate-spin h-8 w-8 text-primary-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span className="text-sm font-medium text-gray-700">Loading dashboard...</span>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 tracking-tight">DASHBOARD - OVERALL SALES REVIEW</h1>
            <p className="text-gray-500 text-sm mt-1">Overview of key sales performance metrics and insights</p>
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Date Filter</label>
              <Select
                options={[
                  { value: 'week', label: 'This Week' },
                  { value: 'month', label: 'This Month' },
                  { value: 'quarter', label: 'This Quarter' },
                  { value: 'last_quarter', label: 'Last Quarter' },
                  { value: 'custom', label: 'Custom Range' },
                ]}
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                className="w-36"
              />
            </div>
            {dateRange === 'custom' && (
              <>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">From</label>
                  <input
                    type="date"
                    value={customStartDate}
                    onChange={(e) => {
                      const newStart = e.target.value;
                      setCustomStartDate(newStart);
                      // If "To" date is before the new "From" date, clear it
                      if (customEndDate && newStart > customEndDate) {
                        setCustomEndDate('');
                        toast.error('"To" date cannot be before "From" date');
                      }
                    }}
                    max={customEndDate || undefined}
                    className="w-32 px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">To</label>
                  <input
                    type="date"
                    value={customEndDate}
                    onChange={(e) => {
                      const newEnd = e.target.value;
                      // Prevent selecting a "To" date that is before the "From" date
                      if (customStartDate && newEnd < customStartDate) {
                        toast.error('"To" date cannot be before "From" date');
                        return;
                      }
                      setCustomEndDate(newEnd);
                    }}
                    min={customStartDate || undefined}
                    className="w-32 px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500"
                  />
                </div>
              </>
            )}
            <div>
              <label className="block text-xs text-gray-500 mb-1">Source Filter</label>
              <Select
                options={sourceOptions}
                value={source}
                onChange={(e) => setSource(e.target.value)}
                className="w-36"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Product Filter</label>
              <Select
                options={productOptions}
                value={product}
                onChange={(e) => setProduct(e.target.value)}
                className="w-36"
              />
            </div>

            <Button
              variant="outline"
              size="sm"
              icon={<HiOutlineDownload className="w-4 h-4" />}
              onClick={handleExport}
            >
              Export
            </Button>
          </div>
        </div>

        {/* Admin Alerts — placed at the very top so it shows directly when the Dashboard opens */}
        <Card padding="p-4 sm:p-6" className="relative overflow-hidden flex flex-col max-h-[450px]">
          <div className="absolute -top-10 -right-10 w-28 h-28 rounded-full bg-red-500 blur-3xl opacity-10 pointer-events-none" />
          <PanelHeader
            icon={<HiOutlineExclamation className="w-4 h-4" />}
            title="Admin Alerts"
            accent="danger"
            right={ceoAlerts.length > 0 ? (
              <span className="flex-shrink-0 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-white text-red-700 shadow-sm">
                {ceoAlerts.length} active
              </span>
            ) : null}
          />
          <div className="space-y-2.5 flex-1 overflow-y-auto">
            {ceoAlerts.length > 0 ? (
              ceoAlerts.map((alert) => (
                <AlertItem
                  key={alert.id}
                  type={alert.type}
                  title={alert.title}
                  description={alert.description}
                  onClick={() => alert.link && navigate(alert.link)}
                />
              ))
            ) : (
              <p className="text-sm text-gray-400 text-center py-6">No alerts right now</p>
            )}
          </div>
          {/* <button
            onClick={() => navigate('/app/alerts')}
            className="flex items-center gap-1 text-sm font-medium text-primary-600 mt-3 pt-3 border-t border-gray-100"
          >
            View all alerts <HiOutlineArrowRight className="w-3.5 h-3.5" />
          </button> */}
        </Card>

        {/* Stat cards row 1 */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard title="Total Leads" value={totalLeads.toLocaleString()} icon={<HiOutlineUsers className="w-5 h-5" />} color="indigo" trend={calculateTrend(stats.totalLeads || 0, prevStatsData?.totalLeads)} onClick={() => navigate('/app/leads')} />
          <StatCard title="Connected Calls" value={callStats?.connected || 0} icon={<HiOutlinePhoneIncoming className="w-5 h-5" />} color="success" trend={calculateTrend(callStats?.connected || 0, prevCallStats?.connected)} onClick={() => navigate('/app/call-logs')} />
          <StatCard title="Not Connected Calls" value={callStats?.notConnected || 0} icon={<HiOutlinePhoneMissedCall className="w-5 h-5" />} color="danger" trend={calculateTrend(callStats?.notConnected || 0, prevCallStats?.notConnected)} onClick={() => navigate('/app/call-logs')} />
          <StatCard title="Connection Rate" value={`${connectionRate}%`} icon={<HiOutlineTrendingUp className="w-5 h-5" />} color="orange" trend={calculateTrend(parseFloat(connectionRate), prevCallStats?.total > 0 ? parseFloat(((prevCallStats?.connected || 0) / prevCallStats.total * 100).toFixed(1)) : 0)} />
        </div>

        {/* Stat cards row 2 */}
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
          <StatCard title="Follow-ups Completed" value={stats?.followUpsCompleted || 0} icon={<HiOutlineCheckCircle className="w-5 h-5" />} color="success" trend={calculateTrend(stats?.followUpsCompleted || 0, prevStatsData?.followUpsCompleted)} onClick={() => navigate('/app/follow-ups?status=completed')} />
          <StatCard title="Pending Follow-ups" value={stats?.pendingFollowUps || leadsCounts?.follow_up || 0} icon={<HiOutlineClock className="w-5 h-5" />} color="orange" trend={calculateTrend(stats?.pendingFollowUps || 0, prevStatsData?.pendingFollowUps)} onClick={() => navigate('/app/follow-ups?status=pending')} />
          <StatCard title="Hot Leads" value={stats?.hotLeads || leadsCounts?.hot || 0} icon={<HiOutlineFire className="w-5 h-5" />} color="danger" trend={calculateTrend(stats?.hotLeads || 0, prevStatsData?.hotLeads)} onClick={() => navigate('/app/leads?status=hot')} />
          <StatCard title="Demo Scheduled" value={stats?.demoScheduled || 0} icon={<HiOutlineClipboardCheck className="w-5 h-5" />} color="indigo" trend={calculateTrend(stats?.demoScheduled || 0, prevStatsData?.demoScheduled)} onClick={() => navigate('/app/leads?stage=meeting_scheduled')} />
          <StatCard title="Converted Leads" value={convertedCount} icon={<HiOutlineSwitchHorizontal className="w-5 h-5" />} color="success" trend={calculateTrend(convertedCount, prevStatsData?.convertedLeads)} onClick={() => navigate('/app/leads?stage=converted')} />
          <StatCard title="Conversion Rate" value={`${conversionRate}%`} icon={<HiOutlineChartPie className="w-5 h-5" />} color="warning" trend={calculateTrend(parseFloat(conversionRate), parseFloat(prevStatsData?.conversionRate || 0))} />
        </div>

        {/* Today's Overview — KPI Grid (all users' performance for the day) */}
        <TodayOverviewKPIGrid />

        {/* Daily Performance Summary */}
        {(() => {
          const dp = dailyPerformanceData || {};
          const summary = dp.summary || {};
          const users = dp.users || [];
          const agentDay = agentDayData || {};
          const agentUsers = agentDay.users || [];

          // Don't render the section if there's no data at all
          if (!summary.totalUsers && users.length === 0 && agentUsers.length === 0) return null;

          // Sort users by total calls descending, then by connected calls descending, limit to top 7
          const topAgents = [...agentUsers]
            .sort((a, b) => (b.calls?.total || 0) - (a.calls?.total || 0) || (b.calls?.connected || 0) - (a.calls?.connected || 0))
            .slice(0, 7);

          const avgConnectionRate = summary.totalCalls > 0
            ? ((summary.totalConnected / summary.totalCalls) * 100).toFixed(1)
            : '0.0';

          const getStatusBadge = (status) => {
            const variants = {
              present: 'success',
              late: 'warning',
              absent: 'danger',
              half_day: 'info',
              early_leave: 'default'
            };
            return variants[status] || 'default';
          };

          return (
            <div className="space-y-4">
              {/* Section Header */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 shadow-md ring-2 bg-gradient-to-br from-green-500 to-green-600 ring-green-500/20">
                    <span className="text-white"><HiOutlineTrendingUp className="w-4 h-4" /></span>
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">Daily Performance Summary</h2>
                    <p className="text-sm text-gray-500">Today's agent activity at a glance</p>
                  </div>
                </div>
              </div>

              {/* Summary Metric Cards */}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                {[
                  { key: 'present', label: 'Present', Icon: HiOutlineUsers, chip: 'from-indigo-500 to-indigo-600 ring-indigo-500/20', bar: 'bg-indigo-500', valueClass: 'text-gray-900', value: <>{summary.present || 0}<span className="text-sm font-normal text-gray-400">/{summary.totalUsers || 0}</span></> },
                  { key: 'absent', label: 'Absent', Icon: HiOutlineUser, chip: 'from-red-500 to-red-600 ring-red-500/20', bar: 'bg-red-500', valueClass: 'text-red-600', value: summary.absent || 0 },
                  { key: 'calls', label: 'Total Calls', Icon: HiOutlinePhone, chip: 'from-blue-500 to-blue-600 ring-blue-500/20', bar: 'bg-blue-500', valueClass: 'text-blue-700', value: summary.totalCalls || 0 },
                  { key: 'connected', label: 'Connected', Icon: HiOutlinePhoneIncoming, chip: 'from-green-500 to-green-600 ring-green-500/20', bar: 'bg-green-500', valueClass: 'text-green-700', value: summary.totalConnected || 0 },
                  { key: 'converted', label: 'Converted', Icon: HiOutlineTrendingUp, chip: 'from-purple-500 to-purple-600 ring-purple-500/20', bar: 'bg-purple-500', valueClass: 'text-purple-700', value: summary.totalConverted || 0 },
                  { key: 'rate', label: 'Avg Connection Rate', Icon: HiOutlineCheckCircle, chip: 'from-amber-500 to-amber-600 ring-amber-500/20', bar: 'bg-amber-500', valueClass: 'text-amber-700', value: `${avgConnectionRate}%` },
                ].map(({ key, label, Icon, chip, bar, valueClass, value }) => (
                  <Card key={key} padding="p-3 lg:p-4" className="relative overflow-hidden text-center hover:-translate-y-0.5 transition-transform duration-200">
                    <div className={`absolute inset-x-0 top-0 h-0.5 ${bar}`} />
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center mx-auto mb-2 shadow-md ring-4 bg-gradient-to-br ${chip}`}>
                      <span className="text-white"><Icon className="w-4 h-4" /></span>
                    </div>
                    <div className={`text-xl font-extrabold tracking-tight ${valueClass}`}>{value}</div>
                    <div className="text-xs text-gray-500 mt-0.5">{label}</div>
                  </Card>
                ))}
              </div>

              {/* Agent Performance Table (Desktop) / Cards (Mobile) */}
              {topAgents.length === 0 && (
                <Card padding="p-0">
                  <div className="px-4 sm:px-6 py-8 text-center">
                    <HiOutlineUser className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">
                      No agent activity recorded for {isAgentToday ? 'today' : format(new Date(agentDate), 'MMM d, yyyy')}
                    </p>
                  </div>
                </Card>
              )}
              {topAgents.length > 0 && (
                <Card padding="p-0">
                  <div className={`flex items-center justify-between gap-3 px-4 sm:px-6 py-3 border-b ${BAND_STYLES.primary}`}>
                    <div className="flex items-center gap-2.5 min-w-0">
                      <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm bg-gradient-to-br ${CHIP_STYLES.primary}`}>
                        <span className="text-white"><HiOutlineUser className="w-4 h-4" /></span>
                      </div>
                      <h3 className="panel-title font-semibold truncate">Top Agent Performance</h3>
                    </div>
                    {/* Date picker — filters this table only */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <HiOutlineCalendar className="w-4 h-4 text-primary-600" />
                      <input
                        type="date"
                        max={todayStr}
                        value={agentDate}
                        onChange={(e) => e.target.value && setAgentDate(e.target.value)}
                        className="px-2 py-1.5 border border-primary-200 rounded-lg text-xs bg-white text-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                      {!isAgentToday && (
                        <button
                          type="button"
                          onClick={() => setAgentDate(todayStr)}
                          className="px-2 py-1.5 rounded-lg text-xs font-medium text-primary-600 bg-white hover:bg-primary-50 transition-colors"
                        >
                          Today
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Desktop Table */}
                  <div className="hidden md:block overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-100">
                          <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">Agent</th>
                          <th className="text-center text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">Attendance</th>
                          <th className="text-center text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">Calls</th>
                          <th className="text-center text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">Connected</th>
                          <th className="text-center text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">Connection Rate</th>
                          <th className="text-center text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">Leads Worked</th>
                          <th className="text-center text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">Converted</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {topAgents.map((user) => (
                          <tr key={user.userId} className="hover:bg-gray-50 transition-colors">
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                                  <span className="text-primary-600 font-medium text-xs">
                                    {user.name?.split(' ').map(n => n[0]).join('')}
                                  </span>
                                </div>
                                <div className="min-w-0">
                                  <p className="text-sm font-medium text-gray-900 truncate">{user.name}</p>
                                  <p className="text-xs text-gray-500 truncate">{user.department}</p>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <Badge variant={getStatusBadge(user.attendance?.status)} size="sm">
                                {formatLabel(user.attendance?.status, 'N/A')}
                              </Badge>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span className="text-sm font-medium text-gray-900">{user.calls?.total || 0}</span>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span className="text-sm font-medium text-green-600">{user.calls?.connected || 0}</span>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center justify-center gap-2">
                                <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                                  <div
                                    className="h-full bg-green-500 rounded-full transition-all duration-500"
                                    style={{ width: `${Math.min(user.calls?.connectionRate || 0, 100)}%` }}
                                  />
                                </div>
                                <span className="text-xs text-gray-600 font-medium">{user.calls?.connectionRate || 0}%</span>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span className="text-sm text-gray-900">{user.leads?.total || 0}</span>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span className="text-sm font-medium text-green-600">{user.leads?.converted || 0}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile Cards */}
                  <div className="md:hidden divide-y divide-gray-100">
                    {topAgents.map((user) => (
                      <div key={user.userId} className="p-4">
                        {/* Header Row */}
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                              <span className="text-primary-600 font-semibold text-sm">
                                {user.name?.split(' ').map(n => n[0]).join('')}
                              </span>
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">{user.name}</p>
                              <p className="text-xs text-gray-500 truncate">{user.department}</p>
                            </div>
                          </div>
                          <Badge variant={getStatusBadge(user.attendance?.status)} size="sm">
                            {formatLabel(user.attendance?.status, 'N/A')}
                          </Badge>
                        </div>

                        {/* Stats Grid */}
                        <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                          <div className="flex items-center gap-2">
                            <HiOutlinePhone className="w-4 h-4 text-gray-400 flex-shrink-0" />
                            <span className="text-gray-700">{user.calls?.total || 0} calls</span>
                            <span className="text-green-600 text-xs">({user.calls?.connected || 0} conn)</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <HiOutlineTrendingUp className="w-4 h-4 text-gray-400 flex-shrink-0" />
                            <span className="text-green-600 font-medium">{user.leads?.converted || 0} converted</span>
                          </div>
                        </div>

                        {/* Connection Rate Bar */}
                        <div className="flex items-center gap-2 mt-2">
                          <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-green-500 rounded-full transition-all duration-300"
                              style={{ width: `${Math.min(user.calls?.connectionRate || 0, 100)}%` }}
                            />
                          </div>
                          <span className="text-xs font-medium text-gray-600 flex-shrink-0">{user.calls?.connectionRate || 0}%</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              )}
            </div>
          );
        })()}


       {/* Charts + Alerts section */}
<div className="space-y-4">

    {/* Top row: Daily Activity Trend + Call Direction Breakdown + Lead Source Performance
        (Admin Alerts moved to the top of the page) */}
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

      {/* Daily/Weekly/Monthly Activity Trend */}
      <Card padding="p-4 sm:p-6" className="relative overflow-hidden">
        <PanelHeader icon={<HiOutlineTrendingUp className="w-4 h-4" />} title={getActivityChartTitle()} accent="info" />
        <ResponsiveContainer width="100%" height={260}>
          <LineChart data={dailyActivityData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{ fontSize: 11 }} />
            <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 11 }} />
            <Tooltip />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Line type="monotone" dataKey="leadsCreated" name="Leads Created" stroke="#3B82F6" strokeWidth={2} dot={{ r: 3 }} />
            <Line type="monotone" dataKey="connectedCalls" name="Connected Calls" stroke="#10B981" strokeWidth={2} dot={{ r: 3 }} />
            <Line type="monotone" dataKey="followUpsCompleted" name="Follow-ups Completed" stroke="#8B5CF6" strokeWidth={2} dot={{ r: 3 }} />
          </LineChart>
        </ResponsiveContainer>
      </Card>

      {/* Inbound vs Outbound Call Breakdown */}
      <Card padding="p-4 sm:p-6" className="relative overflow-hidden">
        <PanelHeader icon={<HiOutlinePhoneIncoming className="w-4 h-4" />} title="Call Direction" accent="success" />
        {(() => {
          const ctData = statsData?.callTypeData || {};
          const outgoing = ctData.outgoing?.count || 0;
          const incoming = ctData.incoming?.count || 0;
          const missed = ctData.missed?.count || 0;
          const total = outgoing + incoming + missed;
          const outgoingConnected = ctData.outgoing?.connected || 0;
          const incomingConnected = ctData.incoming?.connected || 0;

          const directionData = [
            { name: 'Outgoing', value: outgoing, connected: outgoingConnected, color: '#3B82F6' },
            { name: 'Incoming', value: incoming, connected: incomingConnected, color: '#10B981' },
            { name: 'Missed', value: missed, color: '#EF4444' },
          ].filter(d => d.value > 0);

          return directionData.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <HiOutlinePhone className="w-10 h-10 text-gray-300 mb-2" />
              <p className="text-gray-500 text-sm">No call data available</p>
            </div>
          ) : (
            <>
              <div className="relative">
                <ResponsiveContainer width="100%" height={160}>
                  <PieChart>
                    <Pie
                      data={directionData}
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={65}
                      paddingAngle={3}
                      dataKey="value"
                      nameKey="name"
                      animationBegin={0}
                      animationDuration={500}
                    >
                      {directionData.map((entry, index) => (
                        <Cell key={`dir-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value, name) => [value, name]} contentStyle={{ borderRadius: '8px', fontSize: '12px' }} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none" style={{ top: '-10px' }}>
                  <span className="text-xl font-bold text-gray-900">{total.toLocaleString()}</span>
                  <span className="text-xs text-gray-500">Total</span>
                </div>
              </div>
              <div className="mt-2 space-y-1.5">
                {directionData.map((item) => (
                  <div key={item.name} className="flex items-center justify-between text-xs">
                    <span className="flex items-center gap-1.5 text-gray-600">
                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: item.color }} />
                      {item.name}
                    </span>
                    <span className="font-medium text-gray-900">
                      {item.value.toLocaleString()}
                      {item.connected !== undefined && item.value > 0 && (
                        <span className="text-gray-400 font-normal ml-1">
                          ({item.connected} connected)
                        </span>
                      )}
                    </span>
                  </div>
                ))}
              </div>
            </>
          );
        })()}
      </Card>

      {/* Lead Source Performance */}
      <Card padding="p-4 sm:p-6" className="relative overflow-hidden">
        <PanelHeader icon={<HiOutlineChartPie className="w-4 h-4" />} title="Lead Source Performance" accent="indigo" />
        {validLeadSourceData.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <HiOutlineChartPie className="w-10 h-10 text-gray-300 mb-2" />
            <p className="text-gray-500 text-sm">No lead source data available</p>
          </div>
        ) : (
          <>
            <div className="relative">
              {/* Total Leads overlay - positioned behind the chart */}
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none z-10" style={{ top: '-10px' }}>
                <span className="text-xl font-bold text-gray-900">{totalLeadSources.toLocaleString()}</span>
                <span className="text-xs text-gray-500">Total Leads</span>
              </div>
              {/* Chart container */}
              <div className="relative z-20">
                <ResponsiveContainer width="100%" height={210}>
                  <PieChart>
                    <Pie
                      data={validLeadSourceData}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={2}
                      dataKey="value"
                      nameKey="name"
                      animationBegin={0}
                      animationDuration={500}
                      minAngle={1}
                    >
                      {validLeadSourceData.map((entry, index) => (
                        <Cell key={`source-${index}-${entry.name}`} fill={entry.color || PIE_COLORS[index % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value, name) => [value, name]}
                      contentStyle={{ borderRadius: '8px', fontSize: '12px' }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="mt-2 space-y-1.5">
              {validLeadSourceData.map((item, idx) => (
                <div key={item.name || idx} className="flex items-center justify-between text-xs">
                  <span className="flex items-center gap-1.5 text-gray-600">
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: item.color || PIE_COLORS[idx % PIE_COLORS.length] }} />
                    {item.name}
                  </span>
                  <span className="font-medium text-gray-900">
                    {item.value} ({totalLeadSources > 0 ? ((item.value / totalLeadSources) * 100).toFixed(1) : '0.0'}%)
                  </span>
                </div>
              ))}
            </div>
          </>
        )}
      </Card>
    </div>

      </div>

      {/* Quick Start Guide */}
      <QuickStartGuide
        isOpen={quickStartOpen}
        onClose={() => setQuickStartOpen(false)}
      />
    {/* Call Analytics Summary */}
    {(() => {
      const ca = statsData?.callAnalytics || {};
      const statusData = ca.statusBreakdown || {};
      const avgDur = ca.averageDuration || 0;
      const totalTalk = ca.totalTalkTime || 0;
      const maxDur = ca.maxDuration || 0;
      const formatDur = (s) => {
        if (!s) return '0s';
        const m = Math.floor(s / 60);
        const sec = s % 60;
        return m > 0 ? `${m}m ${sec}s` : `${sec}s`;
      };
      const formatHour = (s) => {
        const h = Math.floor(s / 3600);
        const m = Math.floor((s % 3600) / 60);
        return h > 0 ? `${h}h ${m}m` : m > 0 ? `${m}m` : `${Math.round(s)}s`;
      };
      const statusColors = {
        connected: '#10B981', not_connected: '#EF4444', busy: '#F59E0B',
        no_answer: '#F97316', failed: '#EF4444', rejected: '#DC2626', missed: '#F97316',
      };
      const statusLabels = {
        connected: 'Connected', not_connected: 'Not Connected', busy: 'Busy',
        no_answer: 'No Answer', failed: 'Failed', rejected: 'Rejected', missed: 'Missed',
      };
      const pieData = Object.entries(statusData).map(([key, val]) => ({
        name: statusLabels[key] || key, value: val.count, color: statusColors[key] || '#6B7280',
      })).filter(d => d.value > 0);

      // Call Status Breakdown, Call Duration Analytics and Lead Funnel in one row
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mt-4">
          {/* Call Status Breakdown */}
          <Card padding="p-4 sm:p-6" className="relative overflow-hidden">
            <PanelHeader icon={<HiOutlineCheckCircle className="w-4 h-4" />} title="Call Status Breakdown" accent="success" />
            {pieData.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <HiOutlineCheckCircle className="w-10 h-10 text-gray-300 mb-2" />
                <p className="text-gray-500 text-sm">No call data available</p>
              </div>
            ) : (
            <div className="flex items-center gap-4">
              <div className="w-1/2">
                <ResponsiveContainer width="100%" height={180}>
                  <PieChart>
                    <Pie data={pieData} cx="50%" cy="50%" innerRadius={40} outerRadius={70} paddingAngle={2} dataKey="value" nameKey="name" animationBegin={0} animationDuration={500}>
                      {pieData.map((entry, idx) => (
                        <Cell key={`cs-${idx}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value, name) => [value, name]} contentStyle={{ borderRadius: '8px', fontSize: '12px' }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="w-1/2 space-y-2">
                {pieData.map((item) => (
                  <div key={item.name} className="flex items-center justify-between text-xs">
                    <span className="flex items-center gap-1.5 text-gray-600">
                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: item.color }} />
                      {item.name}
                    </span>
                    <span className="font-medium text-gray-900">{item.value}</span>
                  </div>
                ))}
              </div>
            </div>
            )}
          </Card>

          {/* Call Duration Stats */}
          <Card padding="p-4 sm:p-6" className="relative overflow-hidden">
            <PanelHeader icon={<HiOutlineClock className="w-4 h-4" />} title="Call Duration Analytics" accent="info" />
            <div className="grid grid-cols-3 gap-2">
              <div className="text-center p-3 bg-green-50 rounded-xl">
                <p className="text-xs text-gray-500 mb-1">Avg Duration</p>
                <p className="text-lg font-bold text-green-700">{formatDur(avgDur)}</p>
                <p className="text-xs text-gray-400">per connected call</p>
              </div>
              <div className="text-center p-3 bg-blue-50 rounded-xl">
                <p className="text-xs text-gray-500 mb-1">Total Talk Time</p>
                <p className="text-lg font-bold text-blue-700">{formatHour(totalTalk)}</p>
                <p className="text-xs text-gray-400">all calls combined</p>
              </div>
              <div className="text-center p-3 bg-purple-50 rounded-xl">
                <p className="text-xs text-gray-500 mb-1">Longest Call</p>
                <p className="text-lg font-bold text-purple-700">{formatDur(maxDur)}</p>
                <p className="text-xs text-gray-400">max duration</p>
              </div>
            </div>
            {/* Connection rate bar */}
            <div className="mt-4">
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="text-gray-600">Connection Rate</span>
                <span className="font-semibold text-gray-900">{connectionRate}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-green-500 h-2 rounded-full transition-all duration-500"
                  style={{ width: `${Math.min(parseFloat(connectionRate), 100)}%` }}
                />
              </div>
            </div>
          </Card>

          {/* Lead Funnel Overview */}
          <Card padding="p-4 sm:p-6" className="relative overflow-hidden">
            <PanelHeader icon={<HiOutlineUsers className="w-4 h-4" />} title="Lead Funnel Overview" accent="primary" />
            {funnelData.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <HiOutlineUsers className="w-10 h-10 text-gray-300 mb-2" />
                <p className="text-gray-500 text-sm">No funnel data available</p>
              </div>
            ) : (
              <>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={funnelData} layout="vertical" margin={{ left: 10, right: 30 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                    <XAxis type="number" axisLine={false} tickLine={false} tick={{ fontSize: 11 }} />
                    <YAxis dataKey="name" type="category" axisLine={false} tickLine={false} tick={{ fontSize: 11 }} width={100} />
                    <Tooltip
                      formatter={(value, name) => [value, name === 'value' ? 'Leads' : name]}
                      contentStyle={{ borderRadius: '8px', fontSize: '12px' }}
                    />
                    <Bar dataKey="value" name="Leads" radius={[0, 4, 4, 0]} barSize={24}>
                      {funnelData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill || PIE_COLORS[index % PIE_COLORS.length]} />
                      ))}
                      <LabelList dataKey="value" position="right" fontSize={11} fill="#374151" fontWeight={500} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
                {/* Conversion rates between stages */}
                {funnelData.length >= 2 && (
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Stage Conversion Rates</p>
                    <div className="space-y-1">
                      {funnelData.slice(0, -1).map((stage, idx) => {
                        const nextStage = funnelData[idx + 1];
                        const rate = stage.value > 0 ? ((nextStage.value / stage.value) * 100).toFixed(1) : '0.0';
                        const rateNum = parseFloat(rate);
                        const isGood = rateNum >= 50;
                        const isOk = rateNum >= 20;
                        return (
                          <div key={stage.name} className="flex items-center justify-between text-xs">
                            <span className="text-gray-500 truncate">{stage.name} → {nextStage.name}</span>
                            <span className={`font-semibold ${isGood ? 'text-green-600' : isOk ? 'text-yellow-600' : 'text-red-500'}`}>
                              {rate}%
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </>
            )}
          </Card>
        </div>
      );
    })()}
    </div>
    </Layout>
  );
};

export default Dashboard;